import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ExternalLink, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEGAL_VERSION } from "@/legal/content";

/**
 * When a logged-in user hasn't accepted the current LEGAL_VERSION yet,
 * show a sticky banner asking them to review & re-accept. On accept we
 * POST /api/consent which upserts the per-user record (keyed on email),
 * so the banner only shows again on the next LEGAL_VERSION bump.
 */
export default function LegalConsentBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [needsAccept, setNeedsAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  // Wait for the Avacy OIL cookie banner to be dismissed (accepted /
  // rejected / closed) BEFORE showing our legal-consent banner. The two
  // must never appear at the same time.
  const [avacyDismissed, setAvacyDismissed] = useState(false);

  useEffect(() => {
    // Detect ONLY the active OIL modal/overlay (not the small persistent
    // floating shield-icon that remains after opt-in). We check the
    // content overlay + js banner container specifically.
    const oilSelector = ".as-oil-content-overlay, .as-js-oil, .as-oil__preference-panel";
    const isOilOpen = () => {
      const el = document.querySelector(oilSelector);
      if (!el) return false;
      const st = window.getComputedStyle(el);
      return st.display !== "none" && st.visibility !== "hidden" && el.offsetHeight > 20;
    };
    if (!isOilOpen()) {
      setAvacyDismissed(true);
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setAvacyDismissed(true);
      clearInterval(pollId);
      ["oil_optIn", "oil_optLater", "oil_optOut", "oil_hideBanner"]
        .forEach((ev) => document.removeEventListener(ev, finish));
    };
    const pollId = setInterval(() => { if (!isOilOpen()) finish(); }, 300);
    ["oil_optIn", "oil_optLater", "oil_optOut", "oil_hideBanner"]
      .forEach((ev) => document.addEventListener(ev, finish));
    return () => {
      clearInterval(pollId);
      ["oil_optIn", "oil_optLater", "oil_optOut", "oil_hideBanner"]
        .forEach((ev) => document.removeEventListener(ev, finish));
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user?.email) {
      setNeedsAccept(false);
      return;
    }
    (async () => {
      try {
        const r = await api.get("/consent");
        if (cancelled) return;
        const invalid = !r.data?.has_valid_consent;
        const currentIsLatest = r.data?.current_version === LEGAL_VERSION;
        setNeedsAccept(invalid && currentIsLatest);
      } catch {
        /* silent · never block auth on consent check */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  if (!needsAccept || accepted || !avacyDismissed) return null;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await api.post("/consent", {
        terms_accepted: true,
        privacy_accepted: true,
        copyright_confirmed: true,
        version: LEGAL_VERSION,
      });
      setAccepted(true);
      setNeedsAccept(false);
    } catch {
      /* ignore · user can retry */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 pointer-events-none"
      data-testid="legal-consent-banner"
    >
      <div className="max-w-4xl mx-auto pointer-events-auto rounded-xl border border-amber-500/40 bg-[color:var(--bg-elev)]/95 backdrop-blur-md shadow-2xl px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <AlertCircle size={18} className="text-amber-300 flex-shrink-0 mt-0.5 sm:mt-0" />
        <div className="flex-1 text-xs sm:text-sm text-white">
          <div className="font-semibold">
            {t("legal_banner.title", { defaultValue: "Termini e Privacy aggiornati" })}
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-100 border border-amber-500/30 tracking-wide">
              v{LEGAL_VERSION}
            </span>
          </div>
          <div className="text-[color:var(--fg-secondary)] mt-0.5">
            {t("legal_banner.body", {
              defaultValue: "Abbiamo aggiornato Termini di Servizio e Privacy Policy (conformità EU AI Act + GDPR). Rivedi e accetta per continuare.",
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <a
            href="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 text-white border border-white/10"
            data-testid="legal-banner-terms-link"
          >
            <ExternalLink size={11} /> {t("legal_banner.terms", { defaultValue: "Termini" })}
          </a>
          <a
            href="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 text-white border border-white/10"
            data-testid="legal-banner-privacy-link"
          >
            <ExternalLink size={11} /> {t("legal_banner.privacy", { defaultValue: "Privacy" })}
          </a>
          <button
            onClick={handleAccept}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-50"
            data-testid="legal-banner-accept-btn"
          >
            <Check size={12} /> {submitting
              ? t("legal_banner.accepting", { defaultValue: "Accettazione..." })
              : t("legal_banner.accept", { defaultValue: "Accetto" })}
          </button>
        </div>
      </div>
    </div>
  );
}
